/**
 * Calendar Management - Club admin interface for managing club calendars
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import apiService from '../../services/api';
import { AdminPageLayout, ConfirmDialog } from '../../components';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  CreateAction,
  ViewAction
} from '../../components/listing/v2/ListingAction';
import { useToast } from '../../contexts/ToastContext';

const CalendarManagement = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [calendars, setCalendars] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState({}); // Store events for each calendar
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all'
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [calendarToDelete, setCalendarToDelete] = useState(null);

  const calendarTypes = [
    { value: 'team_schedule', label: 'Team Schedule', description: 'Training and practices' },
    { value: 'team_games', label: 'Team Games', description: 'Matches and tournaments' },
    { value: 'holiday', label: 'Holiday Calendar', description: 'Club holidays and closures' },
    { value: 'club_events', label: 'Club Events', description: 'Club-wide events' },
    { value: 'facility', label: 'Facility Calendar', description: 'Facility bookings' },
    { value: 'coaching', label: 'Coaching Calendar', description: 'Coach meetings' },
    { value: 'custom', label: 'Custom Calendar', description: 'Custom purpose' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Load events for all calendars
  const loadCalendarEvents = async (calendarList) => {
    const eventsData = {};

    // Load events for each calendar (limit to upcoming events for preview)
    for (const calendar of calendarList) {
      try {
        const response = await apiService.getCalendarEvents(calendar.id, {
          time_filter: 'upcoming',
          page_size: 2 // Only get next 2 events for preview
        });
        eventsData[calendar.id] = response.events || [];
      } catch (err) {
        console.error(`Error loading events for calendar ${calendar.id}:`, err);
        eventsData[calendar.id] = [];
      }
    }

    setCalendarEvents(eventsData);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const calendarsResponse = await apiService.request('/api/calendars/');

      console.log('[CalendarManagement] Calendars response:', calendarsResponse);
      console.log('[CalendarManagement] First calendar:', calendarsResponse.calendars?.[0]);
      console.log('[CalendarManagement] First calendar ID:', calendarsResponse.calendars?.[0]?.id);

      const calendarList = calendarsResponse.calendars || [];
      setCalendars(calendarList);

      // Load events for each calendar
      if (calendarList.length > 0) {
        await loadCalendarEvents(calendarList);
      }

    } catch (err) {
      console.error('Error loading calendar data:', err);
      setError('Failed to load calendar data');
      showError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };


  const handleDeleteCalendar = async () => {
    if (!calendarToDelete) return;

    try {
      setLoading(true);
      await apiService.request(`/api/calendars/${calendarToDelete.id}`, {
        method: 'DELETE'
      });
      showSuccess(`Calendar "${calendarToDelete.name}" deleted successfully`);
      loadData();
      setShowDeleteConfirm(false);
      setCalendarToDelete(null);
    } catch (err) {
      console.error('Error deleting calendar:', err);
      showError(err.message || 'Failed to delete calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleManageEvents = (calendar) => {
    // Navigate to calendar view with pre-selected calendar (NEW FLOW)
    // This implements the event management flow from CALENDAR_SYSTEM_REFACTOR_PLAN.md
    console.log('[CalendarManagement] Navigating to calendar view with calendar:', calendar.id, calendar);
    navigate(`/calendar?calendar_id=${calendar.id}`);
  };

  const filteredCalendars = calendars.filter(calendar => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!calendar.name.toLowerCase().includes(searchLower) &&
        !(calendar.description?.toLowerCase().includes(searchLower)) &&
        !calendar.scope.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Status filter
    if (filters.status !== 'all') {
      if (filters.status === 'active' && !calendar.is_active) return false;
      if (filters.status === 'inactive' && calendar.is_active) return false;
    }

    return true;
  });

  const getCalendarIcon = (type) => {
    return <Calendar size={32} />;
  };

  const getStatusVariant = (isActive) => {
    return isActive ? 'success' : 'secondary';
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Calendars' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        }
      ]}
      activeFilters={{ status: filters.status }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', status: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction onClick={() => navigate('/admin/calendars/add')} label="New Calendar" />
      }
    />
  );

  const renderCalendars = () => {
    return filteredCalendars;
  };

  return (
    <AdminPageLayout
      title="CALENDAR MANAGEMENT"
      subtitle="Manage club calendars and schedules"
      backPath="/admin/club"
      maxWidth="1400px"
    >
      <Listing
        loading={loading}
        items={filteredCalendars}
        actions={headerActions}
        filters={filterBar}
        renderItem={(calendar) => {
          // Color indicator badge for calendar
          const colorBadge = (
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: calendar.color_code || 'var(--color-primary)',
                border: '2px solid var(--color-background)',
                boxShadow: '0 0 0 1px var(--color-border)',
                flexShrink: 0
              }}
              role="img"
              aria-label={`Calendar color: ${calendar.color_code || 'Blue'}`}
            />
          );
          
          return (
          <ListingItem
            key={calendar.id}
            title={calendar.name}
            subtitle={calendar.scope ? `${calendar.scope.replace(/_/g, ' ')}` : 'Calendar'}
            icon={getCalendarIcon(calendar.scope)}
            iconVariant={getStatusVariant(calendar.is_active)}
            badges={[colorBadge]}
            actions={
              <div style={{ display: 'flex', gap: '8px' }}>
                <ViewAction
                  size="xs"
                  onClick={() => handleManageEvents(calendar)}
                  title="Manage events"
                  aria-label={`Manage events for calendar: ${calendar.name}`}
                />
                <EditAction
                  size="xs"
                  onClick={() => navigate(`/admin/calendars/${calendar.id}/edit`)}
                  aria-label={`Edit calendar: ${calendar.name}`}
                />
                <DeleteAction
                  size="xs"
                  onClick={() => {
                    setCalendarToDelete({ id: calendar.id, name: calendar.name });
                    setShowDeleteConfirm(true);
                  }}
                  aria-label={`Delete calendar: ${calendar.name}`}
                />
              </div>
            }
          >
            <div style={{ display: 'flex', gap: '1rem', marginTop: '0.75rem', fontSize: '0.875rem', flexWrap: 'wrap' }}>
              {calendar.description && (
                <div style={{ color: 'var(--color-text-secondary)', marginTop: '0.5rem', width: '100%' }}>
                  {calendar.description}
                </div>
              )}
              <div>
                <div style={{ color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Status</div>
                <div style={{ fontWeight: '500' }}>
                  {calendar.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>Events</div>
                <div style={{ fontWeight: '500' }}>{calendar.event_count || 0}</div>
              </div>
              {calendar.tags && calendar.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {calendar.tags.map(tag => (
                    <span key={tag} style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', backgroundColor: 'var(--color-background-alt)', borderRadius: '4px', color: 'var(--color-text-secondary)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            {calendarEvents[calendar.id] && calendarEvents[calendar.id].length > 0 && (
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border-light)', fontSize: '0.875rem' }}>
                <div style={{ fontWeight: '500', marginBottom: '0.5rem', color: 'var(--color-text-secondary)' }}>Upcoming Events</div>
                {calendarEvents[calendar.id].slice(0, 2).map(event => {
                  const eventDate = new Date(event.start_time);
                  const formattedDate = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                  const formattedTime = eventDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
                  return (
                    <div key={event.id} style={{ fontSize: '0.8rem', color: 'var(--color-text-tertiary)', marginBottom: '0.25rem' }}>
                      {event.title} - {formattedDate} at {formattedTime}
                    </div>
                  );
                })}
              </div>
            )}
          </ListingItem>
          );
        }}
        emptyMessage="No calendars found"
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setCalendarToDelete(null);
        }}
        onConfirm={handleDeleteCalendar}
        title="Delete Calendar"
        message={`Are you sure you want to delete "${calendarToDelete?.name}"?`}
        details="This action cannot be undone."
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
        loading={loading}
      />
    </AdminPageLayout>
  );
};

export default CalendarManagement;